/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ReactNode, useEffect, useRef } from 'react';

import FocusLock from 'react-focus-lock';

import { getDocument } from '@atlaskit/browser-apis';
import { css, jsx } from '@atlaskit/css';

// display: contents makes the wrapper div layout-transparent — it disappears from the
// box model so PanelContainer's own height/flex styles work exactly as if this wrapper
// were not there. This avoids breaking resize and full-height layouts.
const contentsStyle = { display: 'contents' } as const;

const containerStyles = css({ height: '100%' });

/**
 * Selector for all focusable elements inside a container, in LTR tab order.
 */
const FOCUSABLE_SELECTOR = [
	'a[href]',
	'button:not([disabled])',
	'input:not([disabled])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[tabindex]:not([tabindex="-1"])',
	'details > summary',
	'audio[controls]',
	'video[controls]',
].join(', ');

export type PanelFocusLockProps = {
	/**
	 * Content rendered inside the focus-managed panel.
	 */
	children: ReactNode;

	/**
	 * When `true`, focus trap is enabled so that tabbing will only cycle through elements within the panel.
	 * Use only when the panel is blocking/overlaying the main content, usually with a blanket.
	 *
	 * @default false
	 */
	isFocusLockEnabled?: boolean;
};

/**
 * __PanelFocusLock__
 *
 * Provides focus management for panel-system panels:
 * - Always moves focus to the first interactive element (via react-focus-lock's autoFocus) when the panel opens.
 * - Optionally traps focus inside the panel via `isFocusLockEnabled`.
 * - Returns focus to the previously focused element when the panel closes.
 *
 * This is baked into `PanelContainer`.
 *
 * - [Examples](https://atlassian.design/components/panel-system/examples)
 * - [Code](https://atlassian.design/components/panel-system/code)
 */
export function PanelFocusLock({
	children,
	isFocusLockEnabled = false,
}: PanelFocusLockProps): React.JSX.Element {
	const containerRef = useRef<HTMLDivElement>(null);

	// Capture the element that was focused before the panel opened so we can
	// restore focus to it when the panel closes — regardless of which close
	// path is taken (X button, footer Close, programmatic close, etc.).
	const previouslyFocusedRef = useRef<HTMLElement | null>(null);

	useEffect(() => {
		// Snapshot the currently focused element at mount time.
		previouslyFocusedRef.current = (getDocument()?.activeElement as HTMLElement | null) ?? null;

		return () => {
			// Restore focus on unmount (panel close). Double-rAF ensures this fires
			// after react-focus-lock's own deactivation logic when the lock is active.
			window.requestAnimationFrame(() => {
				window.requestAnimationFrame(() => {
					previouslyFocusedRef.current?.focus();
				});
			});
		};
	}, []);

	// When the lock is NOT enabled, handle auto-focus ourselves — do NOT use
	// react-focus-lock even in disabled mode as it causes layout recalculations
	// that causes animation jank when the panel is inside a CSS transform animation
	// (e.g. SlideIn from @atlaskit/motion).
	useEffect(() => {
		if (isFocusLockEnabled) {
			return;
		}
		const container = containerRef.current;
		if (!container) {
			return;
		}
		// Focus synchronously in useEffect (post-paint) with preventScroll to avoid
		// the browser calling scrollIntoView on a mid-animation element.
		const firstFocusable = container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
		if (firstFocusable) {
			firstFocusable.focus({ preventScroll: true });
		}
	}, [isFocusLockEnabled]);

	// When the lock IS enabled, use react-focus-lock for both autoFocus and the trap.
	if (isFocusLockEnabled) {
		return (
			<FocusLock
				autoFocus
				returnFocus={false}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				lockProps={{ style: contentsStyle }}
			>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop */}
				<div ref={containerRef} style={contentsStyle}>
					{children}
				</div>
			</FocusLock>
		);
	}

	// Default (no lock): plain wrapper div, auto-focus handled by useEffect above.
	// We deliberately avoid react-focus-lock here to prevent animation jank.
	// height: 100% ensures PanelContainer's own height: 100% resolves correctly.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
	return (
		<div ref={containerRef} css={containerStyles}>
			{children}
		</div>
	);
}
